// ============================================================================
// STAKEHOLDER OVERVIEW TAB
// Overview for stakeholder entities surfaced through WorkItemDetailModal.
// Supports contacts, people, locations, and documents with stakeholder-colored
// headers supplied by the parent item_type identity.
// Date: March 2026
// ============================================================================

'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Building2,
  User,
  MapPin,
  FileText,
  Mail,
  Phone,
  Globe,
  Calendar,
  Briefcase,
  Users,
  Hash,
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
  Download,
  Shield,
  CreditCard,
  Tag,
  Package,
  Layers,
  ShieldAlert,
  Pencil,
  Save,
  X,
  Loader2,
  Plus,
  Trash2,
  Edit3,
  Send,
  GitMerge,
  History,
} from 'lucide-react';
import ProductAssignmentModal from '@/components/inventory/ProductAssignmentModal';
import SupplierPerformanceDashboard from '@/components/inventory/SupplierPerformanceDashboard';
import type { WorkItem } from '../UniversalWorkItems';
import GoogleMapsPicker from '@/components/maps/GoogleMapsPicker';
import {
  calculateContactAccountStanding,
  CONTACT_ACCOUNT_STATUS_VALUES,
  formatContactAccountStatusLabel,
  isSupplierStatus,
  isSupplierWarningStatus,
  isSupplierRestrictedStatus,
  formatSupplierStatusLabel,
  SUPPLIER_STATUS_OPTIONS,
  SUPPLIER_STATUS_VALUES,
  normalizeContactPaymentTermsDays,
  type SupplierStatus,
  type ContactFinancialProfile,
} from '@/lib/contacts/accountStanding';
import { authService } from '@/lib/authService';
import { notify } from '@/lib/notifications';
import { invalidateContactAccountCache } from '@/lib/contacts/accountWarning';
import { useModulePermissions } from '@/hooks/useModulePermissions.v2';
import { parseStakeholderEntityWorkItemId } from '@/lib/contacts/stakeholderWorkItemIdentity';
import dynamic from 'next/dynamic';
import type { Bundle as FinanceBundle } from '@/components/finance/modals/BundleDesignerModal';
import ContactSlaOverridesPanel from '@/components/tabs/ContactSlaOverridesPanel';
import VisitorBookingsCard from '@/components/visitors/VisitorBookingsCard';

// Lazy-load heavy management views so they don't bloat the WorkItemDetailModal bundle.
const PriceListManagement = dynamic(
  () => import('@/app/client-zone/modules/total-finance/components/PriceListManagement'),
  {
    ssr: false, loading: () => (
      <div className="flex items-center justify-center py-16 text-sm text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading customer pricing…
      </div>
    )
  }
);
const AdvancedInventoryView = dynamic(
  () => import('@/components/shared/AdvancedInventoryView'),
  {
    ssr: false, loading: () => (
      <div className="flex items-center justify-center py-16 text-sm text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading stock & items…
      </div>
    )
  }
);
const BundleDesignerModal = dynamic(
  () => import('@/components/finance/modals/BundleDesignerModal'),
  {
    ssr: false, loading: () => (
      <div className="flex items-center justify-center py-16 text-sm text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading template designer…
      </div>
    )
  }
);

interface StakeholderOverviewTabProps {
  workItem: WorkItem;
  isDark: boolean;
  onRefresh?: () => void;
  onViewPeople?: () => void;
  onViewLocations?: () => void;
  onViewDocuments?: () => void;
  onManagePricing?: (contactId: number) => void;
  onViewInventory?: (contactId: number) => void;
  onContactUpdated?: (contactId: number, patch: Record<string, unknown>) => void;
  onGrantPortalAccess?: (person: StakeholderPerson) => void;
  onMergeContact?: (contactId: number) => void;
}

const PAYMENT_TERMS_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 0, label: 'Cash on delivery (0 days)' },
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' },
];

const CURRENCY_OPTIONS = ['ZAR', 'USD', 'EUR', 'GBP', 'AUD', 'NZD', 'CAD', 'ZWL'];

type StakeholderEntityType = 'contact' | 'person' | 'location' | 'document';

const OVERVIEW_FIELDS = [
  'company_name',
  'contact_person',
  'email',
  'phone',
  'website',
  'registration_number',
  'tax_number',
  'industry',
  'notes',
] as const;

type StakeholderLocation = {
  id: number;
  location_name: string;
  location_type?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  radius_meters?: number | null;
  is_primary?: boolean;
  is_active?: boolean;
};

type StakeholderPerson = {
  id: number;
  first_name: string;
  last_name: string;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  job_title?: string | null;
  department?: string | null;
  is_primary?: boolean;
  is_active?: boolean;
};

type StakeholderDocument = {
  id: number;
  document_name: string;
  document_type?: string | null;
  description?: string | null;
  file_url?: string | null;
  issue_date?: string | null;
  expiry_date?: string | null;
  is_expired?: boolean;
  is_verified?: boolean;
  location_name?: string | null;
  person_name?: string | null;
};

type AddressLike = {
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
};

type CustomerBundleSummary = FinanceBundle & {
  id: string;
  usage_count?: number;
  updated_at?: string | null;
  created_at?: string | null;
  is_system_template?: boolean;
};

type SupplierRelationship = {
  id: number;
  work_item_id: string;
  supplier_contact_id: number;
  supplier_name?: string | null;
  product_name?: string | null;
  product_sku?: string | null;
  supplier_sku?: string | null;
  cost_price?: number | null;
  currency_code?: string | null;
  lead_time_days?: number | null;
  min_order_qty?: number | null;
  is_preferred: boolean;
  priority_rank?: number | null;
  selection_reason?: string | null;
  fallback_reason?: string | null;
  last_purchase_date?: string | null;
  last_purchase_cost?: number | null;
  notes?: string | null;
};

type StakeholderEntityRecord = {
  [key: string]: unknown;
  id?: number | string | null;
  company_name?: string | null;
  contact_person?: string | null;
  trading_name?: string | null;
  legal_name?: string | null;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  website?: string | null;
  registration_number?: string | null;
  tax_number?: string | null;
  industry?: string | null;
  is_active?: boolean | null;
  account_status?: string | null;
  payment_terms?: string | number | null;
  payment_terms_days?: string | number | null;
  payment_terms_other?: string | null;
  credit_limit?: string | number | null;
  current_balance?: string | number | null;
  currency?: string | null;
  preferred_payment_method?: string | null;
  lead_time_days?: string | number | null;
  minimum_order_value?: string | number | null;
  discount_percentage?: string | number | null;
  bank_name?: string | null;
  bank_account_number?: string | null;
  bank_branch_code?: string | null;
  bank_account_type?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  notes?: string | null;
  description?: string | null;
  document_name?: string | null;
  document_type?: string | null;
  person_name?: string | null;
  file_url?: string | null;
  issue_date?: string | null;
  expiry_date?: string | null;
  is_expired?: boolean | null;
  is_verified?: boolean | null;
  location_name?: string | null;
  location_type?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
  radius_meters?: number | string | null;
  job_title?: string | null;
  department?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  logo_url?: string | null;
  photo_url?: string | null;
};

type StakeholderCustomFields = {
  [key: string]: unknown;
  stakeholder_entity_type?: StakeholderEntityType | string;
  stakeholder_entity_kind?: StakeholderEntityType | string;
  stakeholder_entity?: StakeholderEntityRecord | null;
  stakeholder_contact?: StakeholderEntityRecord | null;
  stakeholder_person?: StakeholderEntityRecord | null;
  stakeholder_location?: StakeholderEntityRecord | null;
  stakeholder_document?: StakeholderEntityRecord | null;
  parent_contact?: StakeholderEntityRecord | null;
  related_people?: StakeholderPerson[];
  related_locations?: StakeholderLocation[];
  related_documents?: StakeholderDocument[];
  assigned_locations?: StakeholderLocation[];
  linked_location?: StakeholderLocation | null;
  linked_person?: StakeholderPerson | null;
  entity_id?: number | string | null;
  account_status?: string | null;
  payment_terms?: string | number | null;
  payment_terms_days?: string | number | null;
  payment_terms_other?: string | null;
  credit_limit?: string | number | null;
  current_balance?: string | number | null;
  currency?: string | null;
  preferred_payment_method?: string | null;
  lead_time_days?: string | number | null;
  minimum_order_value?: string | number | null;
  discount_percentage?: string | number | null;
  bank_name?: string | null;
  bank_account_number?: string | null;
  bank_branch_code?: string | null;
  bank_account_type?: string | null;
  stakeholder_type_label?: string | null;
  open_supplier_items_on_load?: boolean;
};

type LiveContactSnapshot = {
  current_balance: number;
  currency?: string | null;
  credit_limit?: number | null;
  account_status?: string | null;
  is_active?: boolean | null;
  payment_terms?: string | number | null;
  payment_terms_days?: string | number | null;
  payment_terms_other?: string | null;
};

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function normalizeStakeholderLocation<T extends StakeholderLocation>(location: T): T {
  return {
    ...location,
    latitude: toFiniteNumber(location.latitude),
    longitude: toFiniteNumber(location.longitude),
    radius_meters: toFiniteNumber(location.radius_meters),
  };
}

function hasCoordinates(location?: Partial<StakeholderLocation> | null): location is StakeholderLocation {
  return toFiniteNumber(location?.latitude) !== null && toFiniteNumber(location?.longitude) !== null;
}

function formatCoordinates(latitude: unknown, longitude: unknown): string | null {
  const lat = toFiniteNumber(latitude);
  const lng = toFiniteNumber(longitude);

  if (lat === null || lng === null) {
    return null;
  }

  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
}

function formatDate(value?: string | null) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function buildAddress(location?: AddressLike | null) {
  if (!location) return null;
  return [
    location.address_line1,
    location.address_line2,
    location.city,
    location.state,
    location.postal_code,
    location.country,
  ]
    .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
    .join(', ');
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function formatSupplierReason(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function InfoRow({
  icon: Icon,
  label,
  value,
  isDark,
  href,
}: {
  icon: React.ElementType;
  label: string;
  value?: string | null;
  isDark: boolean;
  href?: string;
}) {
  if (!value) return null;

  const content = (
    <div className="flex items-start gap-3 py-2">
      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
      <div className="min-w-0 flex-1">
        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{label}</p>
        <p className={`text-sm break-words ${href ? 'text-blue-500 hover:underline' : isDark ? 'text-gray-200' : 'text-gray-700'}`}>
          {value}
        </p>
      </div>
    </div>
  );

  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {content}
      </a>
    );
  }

  return content;
}

function StatCard({
  icon: Icon,
  label,
  value,
  isDark,
  tone = 'blue',
  onClick,
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  isDark: boolean;
  tone?: 'blue' | 'green' | 'amber' | 'purple' | 'red';
  onClick?: () => void;
}) {
  const toneClass = {
    blue: isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700',
    green: isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700',
    amber: isDark ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-100 text-amber-700',
    purple: isDark ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-700',
    red: isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700',
  }[tone];

  return (
    <div
      className={`rounded-xl border p-4 shadow-sm ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'} ${onClick ? 'cursor-pointer hover:ring-2 hover:ring-blue-500/40 transition-shadow' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); } : undefined}
    >
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${toneClass}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{label}</p>
          <p className={`text-lg font-bold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{value}</p>
        </div>
      </div>
    </div>
  );
}

export default function StakeholderOverviewTab({ workItem, isDark, onRefresh, onViewPeople, onViewLocations, onViewDocuments, onManagePricing, onViewInventory, onContactUpdated, onGrantPortalAccess, onMergeContact }: StakeholderOverviewTabProps) {
  const cf = useMemo(() => {
    if (!workItem.custom_fields) return {} as StakeholderCustomFields;
    return typeof workItem.custom_fields === 'string'
      ? JSON.parse(workItem.custom_fields) as StakeholderCustomFields
      : workItem.custom_fields;
  }, [workItem.custom_fields]);

  const entityType = useMemo(
    () => (cf.stakeholder_entity_type || cf.stakeholder_entity_kind || 'contact') as StakeholderEntityType,
    [cf.stakeholder_entity_type, cf.stakeholder_entity_kind]
  );
  const entity = useMemo(
    () => ((
      cf.stakeholder_entity
      || (entityType === 'contact'
        ? cf.stakeholder_contact
        : entityType === 'person'
          ? cf.stakeholder_person
          : entityType === 'location'
            ? cf.stakeholder_location
            : cf.stakeholder_document)
      || {}
    )) as StakeholderEntityRecord,
    [cf.stakeholder_entity, cf.stakeholder_contact, cf.stakeholder_person, cf.stakeholder_location, cf.stakeholder_document, entityType]
  );
  const parentContact = ((cf.parent_contact || (entityType === 'contact' ? entity : null) || null) as StakeholderEntityRecord | null);
  const relatedPeople = Array.isArray(cf.related_people) ? (cf.related_people as StakeholderPerson[]) : [];
  const relatedLocations = useMemo(
    () => (Array.isArray(cf.related_locations) ? (cf.related_locations as StakeholderLocation[]).map((location) => normalizeStakeholderLocation(location)) : []),
    [cf.related_locations]
  );
  const relatedDocuments = Array.isArray(cf.related_documents) ? (cf.related_documents as StakeholderDocument[]) : [];
  const assignedLocations = useMemo(
    () => (Array.isArray(cf.assigned_locations) ? (cf.assigned_locations as StakeholderLocation[]).map((location) => normalizeStakeholderLocation(location)) : []),
    [cf.assigned_locations]
  );
  const linkedLocation = useMemo(() => {
    if (!cf.linked_location) {
      return null;
    }

    return normalizeStakeholderLocation(cf.linked_location as StakeholderLocation);
  }, [cf.linked_location]);
  const linkedPerson = (cf.linked_person || null) as StakeholderPerson | null;
  const stakeholderContactType = useMemo(() => {
    if (entityType !== 'contact') {
      return null;
    }

    const candidate = String(workItem.item_type || '').trim().toLowerCase();
    return candidate || null;
  }, [entityType, workItem.item_type]);
  const isCustomerStakeholder = stakeholderContactType === 'customer';
  const isSupplierLikeStakeholder = stakeholderContactType === 'supplier';
  const isContractorStakeholder = stakeholderContactType === 'contractor';

  // ── Module permissions: user must be admin (or have can_edit) on at least one module ──
  const crmPerms = useModulePermissions('total-crm');
  const opsPerms = useModulePermissions('total-operations');
  const financePerms = useModulePermissions('total-finance');
  const supportPerms = useModulePermissions('total-support');
  const docAutoPerms = useModulePermissions('total-automation');

  const canEditOverview = useMemo(() => {
    const checks = [crmPerms, opsPerms, financePerms, supportPerms, docAutoPerms];
    return checks.some(({ permissions, accessLevel }) =>
      accessLevel === 'admin' || accessLevel === 'owner' ||
      permissions.can_admin || permissions.can_edit
    );
  }, [crmPerms, opsPerms, financePerms, supportPerms, docAutoPerms]);

  const mapLocations = useMemo(() => {
    if (entityType === 'location' && linkedLocation) {
      return hasCoordinates(linkedLocation) ? [linkedLocation] : [];
    }
    if (entityType === 'person' && assignedLocations.length > 0) {
      return assignedLocations.filter((location) => hasCoordinates(location));
    }
    return relatedLocations.filter((location) => hasCoordinates(location));
  }, [assignedLocations, entityType, linkedLocation, relatedLocations]);

  const mapInitialLocation = mapLocations[0];

  // Local override applied after a successful save so the panel reflects the
  // new values immediately without waiting for the parent to rebuild the
  // virtual stakeholder work item from a refreshed contact bundle.
  const [accountOverride, setAccountOverride] = useState<{
    credit_limit?: number;
    currency?: string;
    payment_terms_days?: number;
    account_status?: string;
  } | null>(null);
  const [liveContactSnapshot, setLiveContactSnapshot] = useState<LiveContactSnapshot | null>(null);
  const [contactFieldOverride, setContactFieldOverride] = useState<Record<string, string | null>>({});
  const [isEditingOverview, setIsEditingOverview] = useState(false);
  const [savingOverview, setSavingOverview] = useState(false);
  const [overviewForm, setOverviewForm] = useState<Record<string, string>>({});

  // Customer portal state for this contact
  const [clientPortalEnabled, setClientPortalEnabled] = useState(false);
  const [contactPortalEnabled, setContactPortalEnabled] = useState(false);
  const [portalToggling, setPortalToggling] = useState(false);

  // Reset the override whenever the underlying work item / entity changes,
  // so re-opening the modal for a different contact starts clean.
  useEffect(() => {
    setAccountOverride(null);
    setContactFieldOverride({});
    setIsEditingOverview(false);
    setOverviewForm({});
    setContactPortalEnabled(false);
  }, [entity?.id, entityType]);

  const contactIdForShortcuts = useMemo<number | null>(() => {
    if (entityType !== 'contact') return null;

    const parsedId = parseStakeholderEntityWorkItemId(String(workItem.id || ''));
    const entityNumericId = typeof entity?.id === 'number' && Number.isFinite(entity.id) && entity.id > 0
      ? entity.id
      : null;

    const candidates: unknown[] = [
      entityNumericId,
      parsedId?.kind === 'contact' ? parsedId.entityId : null,
      (parentContact as Record<string, unknown> | null)?.id,
      cf?.entity_id,
      (workItem as Record<string, unknown>)?.contact_id,
    ];
    for (const c of candidates) {
      const n = typeof c === 'number' ? c : (typeof c === 'string' ? Number(c) : NaN);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return null;
  }, [entityType, entity?.id, cf?.entity_id, parentContact, workItem]);

  // Build account-standing snapshot for stakeholder contacts (customers, suppliers, etc.).
  // For people / locations / documents this stays null and the panel is hidden.
  const accountSnapshot = useMemo(() => {
    if (entityType !== 'contact') return null;

    const profile: ContactFinancialProfile = {
      account_status: accountOverride?.account_status
        ?? liveContactSnapshot?.account_status
        ?? (cf.account_status as string | null | undefined)
        ?? (entity.account_status as string | null | undefined)
        ?? null,
      is_active: liveContactSnapshot?.is_active
        ?? entity.is_active,
      payment_terms: liveContactSnapshot?.payment_terms
        ?? (cf.payment_terms as string | number | null | undefined)
        ?? (entity.payment_terms as string | number | null | undefined)
        ?? null,
      payment_terms_days: accountOverride?.payment_terms_days
        ?? (liveContactSnapshot?.payment_terms_days as string | number | null | undefined)
        ?? (cf.payment_terms_days as string | number | null | undefined)
        ?? (entity.payment_terms_days as string | number | null | undefined)
        ?? null,
      payment_terms_other: liveContactSnapshot?.payment_terms_other
        ?? (cf.payment_terms_other as string | null | undefined)
        ?? (entity.payment_terms_other as string | null | undefined)
        ?? null,
      credit_limit: accountOverride?.credit_limit
        ?? liveContactSnapshot?.credit_limit
        ?? (cf.credit_limit as string | number | null | undefined)
        ?? (entity.credit_limit as string | number | null | undefined)
        ?? null,
      current_balance: liveContactSnapshot?.current_balance
        ?? (cf.current_balance as string | number | null | undefined)
        ?? (entity.current_balance as string | number | null | undefined)
        ?? 0,
      currency: accountOverride?.currency
        ?? liveContactSnapshot?.currency
        ?? (cf.currency as string | null | undefined)
        ?? (entity.currency as string | null | undefined)
        ?? 'ZAR',
    };

    return calculateContactAccountStanding(profile, 0, 'invoice');
  }, [
    accountOverride,
    cf.account_status,
    cf.credit_limit,
    cf.currency,
    cf.current_balance,
    liveContactSnapshot?.account_status,
    liveContactSnapshot?.credit_limit,
    liveContactSnapshot?.currency,
    liveContactSnapshot?.current_balance,
    liveContactSnapshot?.is_active,
    liveContactSnapshot?.payment_terms,
    liveContactSnapshot?.payment_terms_days,
    liveContactSnapshot?.payment_terms_other,
    cf.payment_terms,
    cf.payment_terms_days,
    cf.payment_terms_other,
    entity.account_status,
    entity.credit_limit,
    entity.currency,
    entity.current_balance,
    entity.is_active,
    entity.payment_terms,
    entity.payment_terms_days,
    entity.payment_terms_other,
    entityType,
  ]);

  const accountStandingTone = accountSnapshot?.currentStanding.status === 'blocked'
    ? 'red'
    : accountSnapshot?.currentStanding.status === 'warning'
      ? 'amber'
      : 'green';
  const commercialPanelTitle = isSupplierLikeStakeholder
    ? 'Commercial & Procurement'
    : isContractorStakeholder
      ? 'Commercial & Service Terms'
      : 'Account & Credit';

  const contactHeaderStatusLabel = accountSnapshot
    ? formatContactAccountStatusLabel(accountSnapshot.accountStatus)
    : entity.is_active === false
      ? 'Inactive'
      : 'Active';

  const formatMoney = (amount: number, currency = 'ZAR'): string => {
    try {
      return new Intl.NumberFormat('en-ZA', { style: 'currency', currency }).format(amount);
    } catch {
      return `${currency} ${amount.toFixed(2)}`;
    }
  };

  // -------- Edit mode for Account & Credit panel --------
  const [isEditingAccount, setIsEditingAccount] = useState(false);
  const [savingAccount, setSavingAccount] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [showSupplierItemsModal, setShowSupplierItemsModal] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [showBundleDesignerModal, setShowBundleDesignerModal] = useState(false);
  const [customerTemplates, setCustomerTemplates] = useState<CustomerBundleSummary[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [supplierRelationships, setSupplierRelationships] = useState<SupplierRelationship[]>([]);
  const [loadingSupplierRelationships, setLoadingSupplierRelationships] = useState(false);
  const [supplierRelationshipsError, setSupplierRelationshipsError] = useState<string | null>(null);
  const [productAssignmentModalOpen, setProductAssignmentModalOpen] = useState(false);
  const [showPerformanceDashboard, setShowPerformanceDashboard] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<CustomerBundleSummary | null>(null);
  const [templateActionId, setTemplateActionId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    credit_limit: '',
    currency: 'ZAR',
    payment_terms_days: 30,
    account_status: 'active' as string,
    payment_terms: '30_days' as string,
    payment_terms_other: '',
    preferred_payment_method: '',
    lead_time_days: '',
    minimum_order_value: '',
    discount_percentage: '',
    bank_name: '',
    bank_account_number: '',
    bank_branch_code: '',
    bank_account_type: '',
  });

  const supplierRelationshipStats = useMemo(() => {
    if (supplierRelationships.length === 0) {
      return null;
    }

    const preferredCount = supplierRelationships.filter((relationship) => relationship.is_preferred).length;
    const alternateCount = supplierRelationships.filter((relationship) => !relationship.is_preferred).length;
    const leadTimes = supplierRelationships
      .map((relationship) => relationship.lead_time_days)
      .filter((value): value is number => value != null && Number.isFinite(value));
    const avgLeadTime = leadTimes.length > 0
      ? leadTimes.reduce((sum, value) => sum + value, 0) / leadTimes.length
      : null;
    const lastPurchaseDate = supplierRelationships.reduce<string | null>((latest, relationship) => {
      if (!relationship.last_purchase_date) {
        return latest;
      }

      if (!latest) {
        return relationship.last_purchase_date;
      }

      return new Date(relationship.last_purchase_date).getTime() > new Date(latest).getTime()
        ? relationship.last_purchase_date
        : latest;
    }, null);

    return {
      totalItems: supplierRelationships.length,
      preferredCount,
      alternateCount,
      avgLeadTime,
      lastPurchaseDate,
    };
  }, [supplierRelationships]);

  const sortedSupplierRelationships = useMemo(() => {
    return [...supplierRelationships].sort((left, right) => {
      if (left.is_preferred !== right.is_preferred) {
        return left.is_preferred ? -1 : 1;
      }

      const leftRank = left.priority_rank ?? Number.MAX_SAFE_INTEGER;
      const rightRank = right.priority_rank ?? Number.MAX_SAFE_INTEGER;
      if (leftRank !== rightRank) {
        return leftRank - rightRank;
      }

      const leftPurchase = left.last_purchase_date ? new Date(left.last_purchase_date).getTime() : 0;
      const rightPurchase = right.last_purchase_date ? new Date(right.last_purchase_date).getTime() : 0;
      if (leftPurchase !== rightPurchase) {
        return rightPurchase - leftPurchase;
      }

      return String(left.product_name || '').localeCompare(String(right.product_name || ''));
    });
  }, [supplierRelationships]);

  useEffect(() => {
    if (entityType !== 'contact' || !contactIdForShortcuts) {
      setLiveContactSnapshot(null);
      return;
    }

    let cancelled = false;

    const loadLiveContactSnapshot = async () => {
      try {
        const token = authService.getTokens()?.access_token;
        const response = await fetch(`/api/v1/contacts/${contactIdForShortcuts}?_ts=${encodeURIComponent(String(workItem.updated_at || Date.now()))}`, {
          cache: 'no-store',
          credentials: 'include',
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        const json = await response.json().catch(() => ({}));
        if (!response.ok || json?.success === false) {
          throw new Error(json?.error || `Failed to load contact snapshot (${response.status})`);
        }

        if (cancelled) {
          return;
        }

        const contactData = json?.data && Number(json.data.id) === Number(contactIdForShortcuts)
          ? json.data
          : null;

        setLiveContactSnapshot(contactData ? {
          current_balance: toFiniteNumber(contactData.current_balance) ?? 0,
          currency: typeof contactData.currency === 'string' ? contactData.currency : null,
          credit_limit: toFiniteNumber(contactData.credit_limit),
          account_status: typeof contactData.account_status === 'string' ? contactData.account_status : null,
          is_active: typeof contactData.is_active === 'boolean' ? contactData.is_active : null,
          payment_terms: contactData.payment_terms ?? null,
          payment_terms_days: contactData.payment_terms_days ?? null,
          payment_terms_other: typeof contactData.payment_terms_other === 'string' ? contactData.payment_terms_other : null,
        } : null);

        // Extract portal_enabled from contact data
        if (contactData && typeof contactData.portal_enabled === 'boolean') {
          setContactPortalEnabled(contactData.portal_enabled);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[StakeholderOverview] failed to load live contact snapshot', error);
          setLiveContactSnapshot(null);
        }
      }
    };

    void loadLiveContactSnapshot();

    // Fetch client-level customer portal config
    const loadClientPortalConfig = async () => {
      try {
        const token = authService.getTokens()?.access_token;
        const response = await fetch('/api/v1/client/settings', {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (!response.ok) return;
        const data = await response.json();
        if (data.success && data.data) {
          const portalTemplate = data.data.portal_template || 'classic-grid';
          setClientPortalEnabled(portalTemplate !== 'none');
        }
      } catch {
        // Default to enabled on error
      }
    };

    void loadClientPortalConfig();

    return () => {
      cancelled = true;
    };
  }, [contactIdForShortcuts, entityType, workItem.updated_at]);

  useEffect(() => {
    if (!isSupplierLikeStakeholder || !contactIdForShortcuts) {
      setSupplierRelationships([]);
      setSupplierRelationshipsError(null);
      setLoadingSupplierRelationships(false);
      return;
    }

    let cancelled = false;

    const loadSupplierRelationships = async () => {
      setLoadingSupplierRelationships(true);
      setSupplierRelationshipsError(null);

      try {
        const token = authService.getTokens()?.access_token;
        const response = await fetch(`/api/v1/work-items/inventory/suppliers?supplier_contact_id=${contactIdForShortcuts}`, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        const json = await response.json().catch(() => ({}));
        if (!response.ok || json?.success === false) {
          throw new Error(json?.error || `Failed to load supplied items (${response.status})`);
        }

        if (cancelled) {
          return;
        }

        const rows = Array.isArray(json?.data)
          ? (json.data as Array<Record<string, unknown>>)
          : [];
        setSupplierRelationships(rows.map((row) => ({
          id: Number(row.id),
          work_item_id: String(row.work_item_id || ''),
          supplier_contact_id: Number(row.supplier_contact_id || contactIdForShortcuts),
          supplier_name: typeof row.supplier_name === 'string' ? row.supplier_name : null,
          product_name: typeof row.product_name === 'string' ? row.product_name : null,
          product_sku: typeof row.product_sku === 'string' ? row.product_sku : null,
          supplier_sku: typeof row.supplier_sku === 'string' ? row.supplier_sku : null,
          cost_price: toFiniteNumber(row.cost_price),
          currency_code: typeof row.currency_code === 'string' ? row.currency_code : null,
          lead_time_days: toFiniteNumber(row.lead_time_days),
          min_order_qty: toFiniteNumber(row.min_order_qty),
          is_preferred: Boolean(row.is_preferred),
          priority_rank: toFiniteNumber(row.priority_rank),
          selection_reason: formatSupplierReason(row.selection_reason),
          fallback_reason: formatSupplierReason(row.fallback_reason),
          last_purchase_date: typeof row.last_purchase_date === 'string' ? row.last_purchase_date : null,
          last_purchase_cost: toFiniteNumber(row.last_purchase_cost),
          notes: typeof row.notes === 'string' ? row.notes : null,
        })));
      } catch (error) {
        if (!cancelled) {
          console.error('[StakeholderOverview] failed to load supplier relationships', error);
          setSupplierRelationships([]);
          setSupplierRelationshipsError(getErrorMessage(error, 'Failed to load supplied items'));
        }
      } finally {
        if (!cancelled) {
          setLoadingSupplierRelationships(false);
        }
      }
    };

    void loadSupplierRelationships();

    return () => {
      cancelled = true;
    };
  }, [contactIdForShortcuts, isSupplierLikeStakeholder, workItem.updated_at]);

  useEffect(() => {
    if (!cf.open_supplier_items_on_load || entityType !== 'contact' || !isSupplierLikeStakeholder) {
      return;
    }

    setShowSupplierItemsModal(true);
  }, [cf.open_supplier_items_on_load, entityType, isSupplierLikeStakeholder]);

  const isTemplateAssignedToScopedCustomer = useCallback((template: CustomerBundleSummary) => {
    if (!contactIdForShortcuts) {
      return false;
    }

    if (template.assignment_scope !== 'selected_customers') {
      return false;
    }

    const scopedId = Number(contactIdForShortcuts);
    const assignedContactIds = Array.isArray(template.assigned_contact_ids) ? template.assigned_contact_ids : [];
    const assignedContacts = Array.isArray(template.assigned_contacts) ? template.assigned_contacts : [];

    return assignedContactIds.includes(scopedId)
      || assignedContacts.some((contact) => Number(contact.contact_id) === scopedId)
      || Number(template.default_contact_id) === scopedId;
  }, [contactIdForShortcuts]);

  const fetchCustomerTemplates = useCallback(async () => {
    if (!contactIdForShortcuts) {
      setCustomerTemplates([]);
      return;
    }

    setLoadingTemplates(true);
    try {
      const token = authService.getTokens()?.access_token;
      const params = new URLSearchParams({
        contact_id: String(contactIdForShortcuts),
        include_items: 'true',
        is_active: 'true',
      });

      const response = await fetch(`/api/v1/work-items/bundles?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const json = await response.json().catch(() => ({}));
      if (!response.ok || json?.success === false) {
        throw new Error(json?.error || `Failed to load templates (${response.status})`);
      }

      const bundles = Array.isArray(json?.bundles) ? json.bundles : [];
      setCustomerTemplates(bundles.filter(isTemplateAssignedToScopedCustomer));
    } catch (error: unknown) {
      console.error('[StakeholderOverview] failed to load customer templates', error);
      notify.error(getErrorMessage(error, 'Failed to load customer templates'));
    } finally {
      setLoadingTemplates(false);
    }
  }, [contactIdForShortcuts, isTemplateAssignedToScopedCustomer]);

  useEffect(() => {
    if (!showTemplatesModal) {
      return;
    }

    void fetchCustomerTemplates();
  }, [fetchCustomerTemplates, showTemplatesModal]);

  const handleCreateTemplate = useCallback(() => {
    setEditingTemplate(null);
    setShowBundleDesignerModal(true);
  }, []);

  const handleEditTemplate = useCallback(async (templateId: string) => {
    setTemplateActionId(templateId);
    try {
      const token = authService.getTokens()?.access_token;
      const response = await fetch(`/api/v1/work-items/bundles/${templateId}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const json = await response.json().catch(() => ({}));
      if (!response.ok || json?.success === false || !json?.bundle) {
        throw new Error(json?.error || `Failed to load template (${response.status})`);
      }

      setEditingTemplate(json.bundle);
      setShowBundleDesignerModal(true);
    } catch (error: unknown) {
      console.error('[StakeholderOverview] failed to load template for edit', error);
      notify.error(getErrorMessage(error, 'Failed to open template'));
    } finally {
      setTemplateActionId(null);
    }
  }, []);

  const handleDeleteTemplate = useCallback(async (template: CustomerBundleSummary) => {
    const confirmed = await notify.confirm({
      title: 'Delete Template',
      message: `Delete "${template.name}"? This billing template will no longer be available for this customer. This action cannot be undone.`,
      confirmText: 'Delete Template',
      variant: 'danger',
    });

    if (!confirmed) {
      return;
    }

    setTemplateActionId(template.id);
    try {
      const token = authService.getTokens()?.access_token;
      const response = await fetch(`/api/v1/work-items/bundles/${template.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const json = await response.json().catch(() => ({}));
      if (!response.ok || json?.success === false) {
        throw new Error(json?.error || `Failed to delete template (${response.status})`);
      }

      setCustomerTemplates((prev) => prev.filter((entry) => entry.id !== template.id));
      notify.success('Customer template deleted');
    } catch (error: unknown) {
      console.error('[StakeholderOverview] failed to delete template', error);
      notify.error(getErrorMessage(error, 'Failed to delete template'));
    } finally {
      setTemplateActionId(null);
    }
  }, []);

  useEffect(() => {
    if (!accountSnapshot || isEditingAccount) return;
    const rawDays = (cf.payment_terms_days ?? entity.payment_terms_days ?? cf.payment_terms ?? entity.payment_terms);
    let parsedDays = 30;
    let rawPaymentTerms = '30_days';
    if (typeof rawDays === 'number' && Number.isFinite(rawDays)) {
      parsedDays = rawDays;
    } else if (typeof rawDays === 'string') {
      const presetMap: Record<string, number> = { cod: 0, fob: 0, '7_days': 7, '14_days': 14, '30_days': 30, '60_days': 60, '90_days': 90 };
      if (rawDays in presetMap) {
        parsedDays = presetMap[rawDays];
        rawPaymentTerms = rawDays;
      } else {
        const match = rawDays.match(/\d+/);
        if (match) parsedDays = parseInt(match[0], 10);
      }
    }
    setEditForm({
      credit_limit: accountSnapshot.creditLimit > 0 ? String(accountSnapshot.creditLimit) : '',
      currency: accountSnapshot.currency || 'ZAR',
      payment_terms_days: parsedDays,
      account_status: accountSnapshot.accountStatus || 'active',
      payment_terms: (cf.payment_terms ?? entity.payment_terms) as string || rawPaymentTerms,
      payment_terms_other: (cf.payment_terms_other ?? entity.payment_terms_other) as string || '',
      preferred_payment_method: (cf.preferred_payment_method ?? entity.preferred_payment_method) as string || '',
      lead_time_days: (cf.lead_time_days ?? entity.lead_time_days) ? String(cf.lead_time_days ?? entity.lead_time_days) : '',
      minimum_order_value: (cf.minimum_order_value ?? entity.minimum_order_value) ? String(cf.minimum_order_value ?? entity.minimum_order_value) : '',
      discount_percentage: (cf.discount_percentage ?? entity.discount_percentage) ? String(cf.discount_percentage ?? entity.discount_percentage) : '',
      bank_name: (cf.bank_name ?? entity.bank_name) as string || '',
      bank_account_number: (cf.bank_account_number ?? entity.bank_account_number) as string || '',
      bank_branch_code: (cf.bank_branch_code ?? entity.bank_branch_code) as string || '',
      bank_account_type: (cf.bank_account_type ?? entity.bank_account_type) as string || '',
    });
  }, [accountSnapshot, cf.payment_terms, cf.payment_terms_days, cf.payment_terms_other, cf.preferred_payment_method, cf.lead_time_days, cf.minimum_order_value, cf.discount_percentage, cf.bank_name, cf.bank_account_number, cf.bank_branch_code, cf.bank_account_type, entity.payment_terms, entity.payment_terms_days, entity.payment_terms_other, entity.preferred_payment_method, entity.lead_time_days, entity.minimum_order_value, entity.discount_percentage, entity.bank_name, entity.bank_account_number, entity.bank_branch_code, entity.bank_account_type, isEditingAccount]);

  const handleStartEditOverview = useCallback(() => {
    const getVal = (f: string) => {
      const overridden = contactFieldOverride[f];
      if (overridden !== undefined) return overridden;
      const entityVal = (entity as Record<string, unknown>)[f];
      return typeof entityVal === 'string' ? entityVal : (entityVal ?? '');
    };
    const form: Record<string, string> = {};
    for (const f of OVERVIEW_FIELDS) {
      form[f] = String(getVal(f) ?? '');
    }
    setOverviewForm(form);
    setIsEditingOverview(true);
  }, [contactFieldOverride, entity]);

  const handleSaveOverview = async () => {
    if (!contactIdForShortcuts) return;
    setSavingOverview(true);
    try {
      const patch: Record<string, string | null> = {};
      for (const f of OVERVIEW_FIELDS) {
        const newVal = (overviewForm[f] ?? '').trim();
        const currentVal = contactFieldOverride[f] ?? String((entity as Record<string, unknown>)[f] ?? '');
        if (newVal !== (currentVal ?? '')) {
          patch[f] = newVal || null;
        }
      }
      if (Object.keys(patch).length === 0) {
        setIsEditingOverview(false);
        return;
      }
      const token = authService.getTokens()?.access_token;
      const res = await fetch(`/api/v1/contacts/${contactIdForShortcuts}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(patch),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) {
        throw new Error(json?.error || 'Failed to update contact');
      }
      setContactFieldOverride((prev) => ({ ...prev, ...patch }));
      onContactUpdated?.(contactIdForShortcuts, patch);
      setIsEditingOverview(false);
      notify.success('Contact details updated');
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setSavingOverview(false);
    }
  };

  const handleSaveAccount = async () => {
    if (!contactIdForShortcuts) return;
    const trimmedLimit = editForm.credit_limit.trim();
    const parsedLimit = trimmedLimit === '' ? 0 : Number(trimmedLimit);
    if (Number.isNaN(parsedLimit) || parsedLimit < 0) {
      notify.error('Credit limit must be a non-negative number');
      return;
    }

    setSavingAccount(true);
    try {
      const token = authService.getTokens()?.access_token;
      const resolvedPaymentTermsDays = normalizeContactPaymentTermsDays(
        editForm.payment_terms,
        editForm.payment_terms_other,
      );
      const payload = {
        credit_limit: parsedLimit,
        currency: editForm.currency,
        payment_terms_days: resolvedPaymentTermsDays ?? editForm.payment_terms_days,
        payment_terms: editForm.payment_terms,
        payment_terms_other: editForm.payment_terms_other || undefined,
        preferred_payment_method: editForm.preferred_payment_method || undefined,
        lead_time_days: editForm.lead_time_days ? Number(editForm.lead_time_days) : undefined,
        minimum_order_value: editForm.minimum_order_value ? Number(editForm.minimum_order_value) : undefined,
        discount_percentage: editForm.discount_percentage ? Number(editForm.discount_percentage) : undefined,
        bank_name: editForm.bank_name || undefined,
        bank_account_number: editForm.bank_account_number || undefined,
        bank_branch_code: editForm.bank_branch_code || undefined,
        bank_account_type: editForm.bank_account_type || undefined,
        account_status: editForm.account_status,
      };
      const res = await fetch(`/api/v1/contacts/${contactIdForShortcuts}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) {
        console.error('[StakeholderOverview] save failed', { status: res.status, json });
        throw new Error(json?.error || `Failed to update account (${res.status})`);
      }
      invalidateContactAccountCache(contactIdForShortcuts);
      // Apply optimistic override so the read-only display reflects the new
      // values instantly, even before the parent rebuilds the work item.
      setAccountOverride({
        credit_limit: parsedLimit,
        currency: editForm.currency,
        payment_terms_days: resolvedPaymentTermsDays ?? editForm.payment_terms_days,
        account_status: editForm.account_status,
      });
      notify.success('Account & credit updated');
      setIsEditingAccount(false);
      onContactUpdated?.(contactIdForShortcuts, payload);
    } catch (err: unknown) {
      notify.error(getErrorMessage(err, 'Failed to update account'));
    } finally {
      setSavingAccount(false);
    }
  };

  const handleToggleContactPortal = async (enabled: boolean) => {
    if (!contactIdForShortcuts) return;
    setPortalToggling(true);
    try {
      const token = authService.getTokens()?.access_token;
      const res = await fetch(`/api/v1/contacts/${contactIdForShortcuts}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ portal_enabled: enabled }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) {
        throw new Error(json?.error || 'Failed to update portal setting');
      }
      setContactPortalEnabled(enabled);
      onContactUpdated?.(contactIdForShortcuts, { portal_enabled: enabled });
      notify.success(enabled ? 'Customer portal enabled for this contact' : 'Customer portal disabled for this contact');
    } catch (err: unknown) {
      notify.error(getErrorMessage(err, 'Failed to update portal setting'));
    } finally {
      setPortalToggling(false);
    }
  };

  const entityLabel = {
    contact: 'Stakeholder',
    person: 'Person',
    location: 'Location',
    document: 'Document',
  }[entityType];

  const title =
    entityType === 'person'
      ? `${entity.first_name || ''} ${entity.last_name || ''}`.trim() || workItem.title
      : entity.document_name || entity.location_name || entity.company_name || entity.contact_person || workItem.title;

  const subtitle =
    entityType === 'contact'
      ? entity.contact_person || entity.trading_name || entity.legal_name || null
      : entityType === 'person'
        ? entity.job_title || entity.department || null
        : entityType === 'location'
          ? buildAddress(entity)
          : entity.document_type || null;

  const statusTone =
    entityType === 'document'
      ? entity.is_expired ? 'red' : 'green'
      : entityType === 'contact'
        ? accountStandingTone
        : entity.is_active === false
          ? 'red'
          : 'green';

  const contactDisplayName = entity.company_name || entity.contact_person || title || 'Stakeholder';

  const formatTemplateDate = (value?: string | null) => {
    const formatted = formatDate(value);
    return formatted || '—';
  };

  const formatDocTypeLabel = (docType: string) => docType
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

  const getTemplateScopeMeta = (template: CustomerBundleSummary) => {
    const assignedContacts = Array.isArray(template.assigned_contacts) ? template.assigned_contacts : [];

    if (template.assignment_scope === 'selected_customers') {
      const assignedToCurrentContact = assignedContacts.some((contact) => Number(contact.contact_id) === Number(contactIdForShortcuts));
      return {
        label: assignedToCurrentContact ? 'Assigned to this customer' : 'Customer-specific',
        tone: assignedToCurrentContact ? 'blue' : 'amber',
      } as const;
    }

    return {
      label: 'Shared template',
      tone: 'green',
    } as const;
  };

  const getTemplateBadgeClass = (tone: 'blue' | 'green' | 'amber' | 'gray') => {
    if (tone === 'blue') {
      return isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700';
    }
    if (tone === 'green') {
      return isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700';
    }
    if (tone === 'amber') {
      return isDark ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-100 text-amber-700';
    }
    return isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700';
  };

  const shouldShowInactiveBanner = entityType === 'contact' &&
    (accountSnapshot?.accountStatus === 'draft' ||
      accountSnapshot?.accountStatus === 'suspended_non_payment' ||
      accountSnapshot?.accountStatus === 'suspended_over_limit' ||
      accountSnapshot?.accountStatus === 'on_hold' ||
      isSupplierRestrictedStatus(accountSnapshot?.accountStatus) ||
      (entity.is_active === false && !accountSnapshot));

  return (
    <div className="space-y-6" data-tour-id="stakeholder-overview-tab">
      {/* Prominent inactive status banner */}
      {shouldShowInactiveBanner && (
        <div className={`rounded-lg border-l-4 p-4 ${isDark ? 'bg-amber-900/20 border-amber-500' : 'bg-amber-50 border-amber-500'}`}>
          <div className="flex items-start gap-3">
            <AlertTriangle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
            <div className="flex-1">
              <p className={`text-sm font-semibold ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>
                Contact is {accountSnapshot ? formatContactAccountStatusLabel(accountSnapshot.accountStatus) : 'Inactive'}
              </p>
              <p className={`text-xs mt-1 ${isDark ? 'text-amber-400/80' : 'text-amber-700'}`}>
                This contact cannot be used for operations until the account status is updated to Active.
              </p>
            </div>
          </div>
        </div>
      )}
      <div className={`rounded-xl border p-5 ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-4">
            {/* Logo / Photo */}
            {entity.logo_url || entity.photo_url ? (
              /* eslint-disable-next-line @next/next/no-img-element */
              <img
                src={(entity.logo_url || entity.photo_url) as string}
                alt={entity.company_name || entity.contact_person || 'logo'}
                className={`flex-shrink-0 rounded-lg object-contain border ${isDark ? 'border-gray-600 bg-gray-900/50' : 'border-gray-200 bg-white'}`}
                style={{ width: 56, height: 56 }}
              />
            ) : (
              <div className={`flex-shrink-0 rounded-lg flex items-center justify-center border ${isDark ? 'border-gray-600 bg-gray-700/50' : 'border-gray-200 bg-white'}`} style={{ width: 56, height: 56 }}>
                {entityType === 'person' ? (
                  <User className={`w-7 h-7 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                ) : (
                  <Building2 className={`w-7 h-7 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                )}
              </div>
            )}
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${isDark ? 'bg-white/10 text-white' : 'bg-gray-900 text-white'}`}>
                  {entityLabel}
                </span>
                <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${statusTone === 'green' ? (isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700') : (isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700')}`} data-tour-id={entityType === 'contact' ? 'stakeholder-status-badge' : undefined}>
                  {entityType === 'document'
                    ? entity.is_expired ? 'Expired' : 'Active'
                    : entityType === 'contact'
                      ? contactHeaderStatusLabel
                      : entity.is_active === false ? 'Inactive' : 'Active'}
                </span>
                {cf.stakeholder_type_label && (
                  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                    {cf.stakeholder_type_label}
                  </span>
                )}
              </div>
              <h2 className={`mt-3 text-2xl font-bold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{title}</h2>
              {subtitle && (
                <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{subtitle}</p>
              )}
            </div>
          </div>

          {entityType === 'document' && entity.file_url ? (
            <div className="flex items-center gap-2">
              <a
                href={entity.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${isDark ? 'bg-blue-600 text-white hover:bg-blue-500' : 'bg-blue-500 text-white hover:bg-blue-600'}`}
              >
                <ExternalLink className="w-4 h-4" />
                Open File
              </a>
              <a
                href={entity.file_url}
                download
                className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${isDark ? 'bg-gray-700 text-gray-100 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                <Download className="w-4 h-4" />
                Download
              </a>
            </div>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-tour-id="stakeholder-overview-stats">
        <StatCard icon={Users} label="People" value={entityType === 'person' ? 1 : relatedPeople.length} isDark={isDark} tone="blue" onClick={onViewPeople} />
        <StatCard icon={MapPin} label="Locations" value={entityType === 'location' ? 1 : entityType === 'person' ? assignedLocations.length : relatedLocations.length} isDark={isDark} tone="green" onClick={onViewLocations} />
        <StatCard icon={FileText} label="Documents" value={entityType === 'document' ? 1 : relatedDocuments.length} isDark={isDark} tone="purple" onClick={onViewDocuments} />
        <StatCard
          icon={Calendar}
          label={entityType === 'document' ? 'Expiry' : 'Created'}
          value={entityType === 'document' ? (formatDate(entity.expiry_date) || '—') : (formatDate(entity.created_at) || '—')}
          isDark={isDark}
          tone={entityType === 'document' && entity.is_expired ? 'red' : 'amber'}
        />
        {entityType !== 'document' && (
          <StatCard
            icon={Edit3}
            label="Last Edited"
            value={formatDate(entity.updated_at) || '—'}
            isDark={isDark}
            tone="blue"
          />
        )}
      </div>

      {stakeholderContactType === 'visitor' && contactIdForShortcuts !== null && (
        <VisitorBookingsCard contactId={contactIdForShortcuts} isDark={isDark} />
      )}

      {accountSnapshot && (
        <div className={`rounded-xl border ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`} data-tour-id="stakeholder-account-panel">
          <div className={`px-4 py-3 border-b flex items-center justify-between gap-3 ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
            <h3 className={`text-sm font-semibold flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
              <CreditCard className="w-4 h-4 text-blue-500" />
              {commercialPanelTitle}
            </h3>
            <div className="flex items-center gap-2">
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${accountStandingTone === 'red'
                  ? (isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700')
                  : accountStandingTone === 'amber'
                    ? (isDark ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-100 text-amber-700')
                    : (isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700')
                  }`}
              >
                {accountStandingTone === 'red' ? <ShieldAlert className="w-3.5 h-3.5" /> : accountStandingTone === 'amber' ? <AlertTriangle className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                {formatContactAccountStatusLabel(accountSnapshot.accountStatus)}
              </span>
              {contactIdForShortcuts !== null && !isEditingAccount && (
                <button
                  type="button"
                  onClick={() => setIsEditingAccount(true)}
                  className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium ${isDark ? 'bg-gray-700 text-gray-100 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  title="Edit credit limit, currency, payment terms, and account status"
                  data-tour-id="stakeholder-account-edit"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  Edit
                </button>
              )}
              {isEditingAccount && (
                <>
                  <button
                    type="button"
                    onClick={() => setIsEditingAccount(false)}
                    disabled={savingAccount}
                    className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium ${isDark ? 'bg-gray-700 text-gray-100 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} disabled:opacity-50`}
                  >
                    <X className="w-3.5 h-3.5" />
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveAccount}
                    disabled={savingAccount}
                    className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-white ${isDark ? 'bg-blue-600 hover:bg-blue-500' : 'bg-blue-500 hover:bg-blue-600'} disabled:opacity-50`}
                  >
                    {savingAccount ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    Save
                  </button>
                </>
              )}
            </div>
          </div>
          {!isEditingAccount ? (
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Approved Credit Limit</p>
                  <p className={`text-lg font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                    {accountSnapshot.creditLimit > 0 ? formatMoney(accountSnapshot.creditLimit, accountSnapshot.currency) : 'No credit limit set'}
                  </p>
                </div>
                <div>
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Current Balance</p>
                  <p className={`text-lg font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                    {formatMoney(accountSnapshot.currentBalance, accountSnapshot.currency)}
                  </p>
                </div>
                <div>
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Available Credit</p>
                  <p className={`text-lg font-semibold ${accountSnapshot.availableCredit < 0 ? 'text-red-500' : isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                    {accountSnapshot.creditLimit > 0 ? formatMoney(accountSnapshot.availableCredit, accountSnapshot.currency) : '—'}
                  </p>
                </div>
                <div>
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Payment Terms</p>
                  <p className={`text-lg font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                    {accountSnapshot.paymentTermsLabel}
                  </p>
                </div>
              </div>

              {(cf.preferred_payment_method || cf.lead_time_days || cf.minimum_order_value || cf.discount_percentage || cf.bank_name) && (
                <div className={`pt-3 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {cf.preferred_payment_method && (
                      <div>
                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Preferred Payment Method</p>
                        <p className={`text-sm ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{cf.preferred_payment_method}</p>
                      </div>
                    )}
                    {cf.lead_time_days && (
                      <div>
                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Lead Time</p>
                        <p className={`text-sm ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{cf.lead_time_days} days</p>
                      </div>
                    )}
                    {cf.minimum_order_value && (
                      <div>
                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Minimum Order Value</p>
                        <p className={`text-sm ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{formatMoney(Number(cf.minimum_order_value), accountSnapshot.currency)}</p>
                      </div>
                    )}
                    {cf.discount_percentage && (
                      <div>
                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Discount Percentage</p>
                        <p className={`text-sm ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{cf.discount_percentage}%</p>
                      </div>
                    )}
                  </div>

                  {cf.bank_name && (
                    <div className="mt-3">
                      <p className={`text-xs font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Bank Details</p>
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {cf.bank_name && (
                          <div>
                            <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Bank Name</p>
                            <p className={`text-sm ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{cf.bank_name}</p>
                          </div>
                        )}
                        {cf.bank_account_number && (
                          <div>
                            <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Account Number</p>
                            <p className={`text-sm ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{cf.bank_account_number}</p>
                          </div>
                        )}
                        {cf.bank_branch_code && (
                          <div>
                            <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Branch Code</p>
                            <p className={`text-sm ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{cf.bank_branch_code}</p>
                          </div>
                        )}
                        {cf.bank_account_type && (
                          <div>
                            <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Account Type</p>
                            <p className={`text-sm ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{cf.bank_account_type}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Payment Terms</label>
                  <select
                    value={editForm.payment_terms}
                    onChange={(e) => {
                      const nextTerms = e.target.value;
                      const nextDays = normalizeContactPaymentTermsDays(nextTerms, editForm.payment_terms_other);
                      setEditForm((prev) => ({
                        ...prev,
                        payment_terms: nextTerms,
                        payment_terms_days: nextDays ?? prev.payment_terms_days,
                        payment_terms_other: nextTerms === 'other' ? prev.payment_terms_other : '',
                      }));
                    }}
                    className={`w-full rounded-md border px-3 py-2 text-sm ${isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                  >
                    <option value="cod">COD</option>
                    <option value="fob">FOB</option>
                    <option value="7_days">7 Days</option>
                    <option value="14_days">14 Days</option>
                    <option value="30_days">30 Days</option>
                    <option value="60_days">60 Days</option>
                    <option value="90_days">90 Days</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                {editForm.payment_terms === 'other' ? (
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Custom Terms</label>
                    <input
                      type="text"
                      value={editForm.payment_terms_other}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, payment_terms_other: e.target.value }))}
                      placeholder="e.g. 45 days from statement"
                      className={`w-full rounded-md border px-3 py-2 text-sm ${isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                    />
                  </div>
                ) : (
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Preferred Payment Method</label>
                    <input
                      type="text"
                      value={editForm.preferred_payment_method}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, preferred_payment_method: e.target.value }))}
                      placeholder="EFT, debit order, card, etc."
                      className={`w-full rounded-md border px-3 py-2 text-sm ${isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                    />
                  </div>
                )}
                <div>
                  <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Approved Credit Limit</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editForm.credit_limit}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, credit_limit: e.target.value }))}
                    placeholder="0.00"
                    className={`w-full rounded-md border px-3 py-2 text-sm ${isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                  />
                  <p className={`mt-1 text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Set 0 to remove credit limit</p>
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Currency</label>
                  <select
                    value={editForm.currency}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, currency: e.target.value }))}
                    className={`w-full rounded-md border px-3 py-2 text-sm ${isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                  >
                    {CURRENCY_OPTIONS.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {isSupplierLikeStakeholder ? 'Supplier Status' : 'Account Status'}
                  </label>
                  <select
                    value={editForm.account_status}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, account_status: e.target.value }))}
                    className={`w-full rounded-md border px-3 py-2 text-sm ${isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                  >
                    {isSupplierLikeStakeholder ? (
                      <>
                        {/* Supplier-specific statuses */}
                        {SUPPLIER_STATUS_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                        {/* Also include basic customer statuses for suppliers */}
                        <option value="active">Active</option>
                        <option value="on_hold">On Hold</option>
                        <option value="inactive">Inactive</option>
                      </>
                    ) : (
                      CONTACT_ACCOUNT_STATUS_VALUES.map((s) => (
                        <option key={s} value={s}>{formatContactAccountStatusLabel(s)}</option>
                      ))
                    )}
                  </select>
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Lead Time (days)</label>
                  <input
                    type="number"
                    min="0"
                    value={editForm.lead_time_days}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, lead_time_days: e.target.value }))}
                    placeholder="0"
                    className={`w-full rounded-md border px-3 py-2 text-sm ${isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                  />
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Minimum Order Value</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editForm.minimum_order_value}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, minimum_order_value: e.target.value }))}
                    placeholder="0.00"
                    className={`w-full rounded-md border px-3 py-2 text-sm ${isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                  />
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Discount Percentage</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={editForm.discount_percentage}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, discount_percentage: e.target.value }))}
                    placeholder="0"
                    className={`w-full rounded-md border px-3 py-2 text-sm ${isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                  />
                </div>
              </div>

              <div className={`pt-3 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <p className={`text-xs font-medium mb-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Bank Details</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Bank Name</label>
                    <input
                      type="text"
                      value={editForm.bank_name}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, bank_name: e.target.value }))}
                      placeholder="Bank name"
                      className={`w-full rounded-md border px-3 py-2 text-sm ${isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Account Number</label>
                    <input
                      type="text"
                      value={editForm.bank_account_number}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, bank_account_number: e.target.value }))}
                      placeholder="Account number"
                      className={`w-full rounded-md border px-3 py-2 text-sm ${isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Branch Code</label>
                    <input
                      type="text"
                      value={editForm.bank_branch_code}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, bank_branch_code: e.target.value }))}
                      placeholder="Branch code"
                      className={`w-full rounded-md border px-3 py-2 text-sm ${isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Account Type</label>
                    <select
                      value={editForm.bank_account_type}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, bank_account_type: e.target.value }))}
                      className={`w-full rounded-md border px-3 py-2 text-sm ${isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                    >
                      <option value="">Select...</option>
                      <option value="cheque">Cheque</option>
                      <option value="savings">Savings</option>
                      <option value="current">Current</option>
                      <option value="transmission">Transmission</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}
          {accountSnapshot.currentStanding.status !== 'good' && (
            <div className={`px-4 pb-4`}>
              <div className={`rounded-lg p-3 text-sm ${accountStandingTone === 'red'
                ? (isDark ? 'bg-red-900/20 text-red-200' : 'bg-red-50 text-red-700')
                : (isDark ? 'bg-amber-900/20 text-amber-200' : 'bg-amber-50 text-amber-700')
                }`}>
                <p className="font-medium">{accountSnapshot.currentStanding.message}</p>
                {accountSnapshot.currentStanding.reasons.length > 0 && (
                  <ul className="mt-1 text-xs space-y-0.5 list-disc list-inside">
                    {accountSnapshot.currentStanding.reasons.map((reason, idx) => (
                      <li key={idx}>{reason}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
          {contactIdForShortcuts !== null && isCustomerStakeholder && clientPortalEnabled && (
            <div className={`px-4 pb-4 border-t pt-3 ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
              <div className={`rounded-lg p-3 flex items-center justify-between ${contactPortalEnabled ? (isDark ? 'bg-teal-900/20 border border-teal-800' : 'bg-teal-50 border border-teal-200') : (isDark ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50 border border-gray-200')}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${contactPortalEnabled ? 'bg-teal-500' : 'bg-gray-400'}`}>
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>Customer Portal Access</p>
                    <p className={`text-xs ${contactPortalEnabled ? (isDark ? 'text-teal-400' : 'text-teal-700') : (isDark ? 'text-gray-500' : 'text-gray-500')}`}>
                      {contactPortalEnabled ? 'Enabled — contact people can be invited to the portal' : 'Disabled — invite contact people to enable portal access'}
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={contactPortalEnabled}
                    onChange={(e) => void handleToggleContactPortal(e.target.checked)}
                    disabled={portalToggling}
                    className="sr-only peer"
                  />
                  <div className={`w-11 h-6 rounded-full transition-colors ${contactPortalEnabled ? 'bg-teal-500' : 'bg-gray-300 dark:bg-gray-600'} ${portalToggling ? 'opacity-50' : ''}`}>
                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform mt-0.5 ${contactPortalEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </div>
                </label>
              </div>
            </div>
          )}
          {contactIdForShortcuts !== null && (isCustomerStakeholder || isSupplierLikeStakeholder) && (
            <div className={`px-4 pb-4 flex flex-wrap gap-2 border-t pt-3 ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
              {isCustomerStakeholder && onManagePricing && (
                <button
                  type="button"
                  onClick={() => setShowPricingModal(true)}
                  className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${isDark
                    ? 'bg-blue-600 text-white hover:bg-blue-500'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                    }`}
                >
                  <Tag className="w-4 h-4" />
                  Customer Pricing
                </button>
              )}
              {isCustomerStakeholder && (
                <button
                  type="button"
                  onClick={() => setShowTemplatesModal(true)}
                  className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${isDark
                    ? 'bg-purple-600 text-white hover:bg-purple-500'
                    : 'bg-purple-500 text-white hover:bg-purple-600'
                    }`}
                >
                  <Layers className="w-4 h-4" />
                  Templates
                </button>
              )}
              {isCustomerStakeholder && onViewInventory && (
                <button
                  type="button"
                  onClick={() => setShowInventoryModal(true)}
                  className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${isDark
                    ? 'bg-gray-700 text-gray-100 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  <Package className="w-4 h-4" />
                  Items &amp; Inventory
                </button>
              )}
              {isSupplierLikeStakeholder && (
                <button
                  type="button"
                  onClick={() => setShowSupplierItemsModal(true)}
                  className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${isDark
                    ? 'bg-teal-600 text-white hover:bg-teal-500'
                    : 'bg-teal-500 text-white hover:bg-teal-600'
                    }`}
                >
                  <Package className="w-4 h-4" />
                  Supplied Items
                </button>
              )}
              {isSupplierLikeStakeholder && contactIdForShortcuts !== null && (
                <button
                  type="button"
                  onClick={() => {
                    if (typeof window !== 'undefined') {
                      window.dispatchEvent(new CustomEvent('navigate-to-supplier-history', {
                        detail: { contactId: contactIdForShortcuts },
                      }));
                    }
                  }}
                  className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${isDark
                    ? 'bg-emerald-700 text-white hover:bg-emerald-600'
                    : 'bg-emerald-600 text-white hover:bg-emerald-700'
                    }`}
                  data-tour-id="stakeholder-view-account-history"
                  title="View supplier account history (bills, payments, remittances)"
                >
                  <History className="w-4 h-4" />
                  Account History
                </button>
              )}
              {onMergeContact && contactIdForShortcuts !== null && (
                <button
                  type="button"
                  onClick={() => onMergeContact(contactIdForShortcuts)}
                  className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${isDark
                    ? 'bg-indigo-600 text-white hover:bg-indigo-500'
                    : 'bg-indigo-500 text-white hover:bg-indigo-600'
                    }`}
                  data-tour-id="stakeholder-merge-button"
                >
                  <GitMerge className="w-4 h-4" />
                  Merge
                </button>
              )}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[1.15fr_0.85fr] gap-6">
        <div className="space-y-6">
          <div className={`rounded-xl border ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className={`px-4 py-3 border-b flex items-center justify-between gap-3 ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
              <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>Overview</h3>
              {entityType === 'contact' && contactIdForShortcuts !== null && canEditOverview && !isEditingOverview && (
                <button
                  type="button"
                  onClick={handleStartEditOverview}
                  className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium ${isDark ? 'bg-gray-700 text-gray-100 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  title="Edit contact details"
                  data-tour-id="stakeholder-overview-edit"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  Edit
                </button>
              )}
              {entityType === 'contact' && isEditingOverview && (
                <>
                  <button
                    type="button"
                    onClick={() => setIsEditingOverview(false)}
                    disabled={savingOverview}
                    className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium ${isDark ? 'bg-gray-700 text-gray-100 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} disabled:opacity-50`}
                  >
                    <X className="w-3.5 h-3.5" />
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveOverview}
                    disabled={savingOverview}
                    className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-white ${isDark ? 'bg-blue-600 hover:bg-blue-500' : 'bg-blue-500 hover:bg-blue-600'} disabled:opacity-50`}
                  >
                    {savingOverview ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    Save
                  </button>
                </>
              )}
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-0 md:gap-x-6">
              {entityType === 'contact' && !isEditingOverview && (
                <>
                  <InfoRow icon={Building2} label="Company" value={contactFieldOverride.company_name ?? entity.company_name} isDark={isDark} />
                  <InfoRow icon={User} label="Contact Person" value={contactFieldOverride.contact_person ?? entity.contact_person} isDark={isDark} />
                  <InfoRow icon={Mail} label="Email" value={contactFieldOverride.email ?? entity.email} isDark={isDark} href={(contactFieldOverride.email ?? entity.email) ? `mailto:${contactFieldOverride.email ?? entity.email}` : undefined} />
                  <InfoRow icon={Phone} label="Phone" value={contactFieldOverride.phone ?? entity.phone ?? entity.mobile} isDark={isDark} href={(contactFieldOverride.phone ?? entity.phone ?? entity.mobile) ? `tel:${contactFieldOverride.phone ?? entity.phone ?? entity.mobile}` : undefined} />
                  <InfoRow icon={Globe} label="Website" value={contactFieldOverride.website ?? entity.website} isDark={isDark} href={(contactFieldOverride.website ?? entity.website) || undefined} />
                  <InfoRow icon={Hash} label="Account #" value={(entity as Record<string, unknown>).contact_number as string | null | undefined} isDark={isDark} />
                  <InfoRow icon={Hash} label="Registration Number" value={contactFieldOverride.registration_number ?? entity.registration_number} isDark={isDark} />
                  <InfoRow icon={Shield} label="Tax Number" value={contactFieldOverride.tax_number ?? entity.tax_number} isDark={isDark} />
                  <InfoRow icon={Briefcase} label="Industry" value={contactFieldOverride.industry ?? entity.industry} isDark={isDark} />
                </>
              )}
              {entityType === 'contact' && isEditingOverview && (
                <>
                  <div className="flex items-start gap-3 py-2">
                    <Building2 className={`w-4 h-4 mt-1.5 flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                    <div className="min-w-0 flex-1">
                      <label className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Company</label>
                      <input
                        type="text"
                        value={overviewForm.company_name ?? ''}
                        onChange={(e) => setOverviewForm((f) => ({ ...f, company_name: e.target.value }))}
                        disabled={savingOverview}
                        className={`w-full rounded-md border px-2 py-1 text-sm ${isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                      />
                    </div>
                  </div>
                  <div className="flex items-start gap-3 py-2">
                    <User className={`w-4 h-4 mt-1.5 flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                    <div className="min-w-0 flex-1">
                      <label className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Contact Person</label>
                      <input
                        type="text"
                        value={overviewForm.contact_person ?? ''}
                        onChange={(e) => setOverviewForm((f) => ({ ...f, contact_person: e.target.value }))}
                        disabled={savingOverview}
                        className={`w-full rounded-md border px-2 py-1 text-sm ${isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                      />
                    </div>
                  </div>
                  <div className="flex items-start gap-3 py-2">
                    <Mail className={`w-4 h-4 mt-1.5 flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                    <div className="min-w-0 flex-1">
                      <label className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Email</label>
                      <input
                        type="text"
                        value={overviewForm.email ?? ''}
                        onChange={(e) => setOverviewForm((f) => ({ ...f, email: e.target.value }))}
                        disabled={savingOverview}
                        className={`w-full rounded-md border px-2 py-1 text-sm ${isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                      />
                    </div>
                  </div>
                  <div className="flex items-start gap-3 py-2">
                    <Phone className={`w-4 h-4 mt-1.5 flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                    <div className="min-w-0 flex-1">
                      <label className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Phone</label>
                      <input
                        type="text"
                        value={overviewForm.phone ?? ''}
                        onChange={(e) => setOverviewForm((f) => ({ ...f, phone: e.target.value }))}
                        disabled={savingOverview}
                        className={`w-full rounded-md border px-2 py-1 text-sm ${isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                      />
                    </div>
                  </div>
                  <div className="flex items-start gap-3 py-2">
                    <Globe className={`w-4 h-4 mt-1.5 flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                    <div className="min-w-0 flex-1">
                      <label className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Website</label>
                      <input
                        type="text"
                        value={overviewForm.website ?? ''}
                        onChange={(e) => setOverviewForm((f) => ({ ...f, website: e.target.value }))}
                        disabled={savingOverview}
                        className={`w-full rounded-md border px-2 py-1 text-sm ${isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                      />
                    </div>
                  </div>
                  <div className="flex items-start gap-3 py-2">
                    <Hash className={`w-4 h-4 mt-1.5 flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                    <div className="min-w-0 flex-1">
                      <label className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Registration Number</label>
                      <input
                        type="text"
                        value={overviewForm.registration_number ?? ''}
                        onChange={(e) => setOverviewForm((f) => ({ ...f, registration_number: e.target.value }))}
                        disabled={savingOverview}
                        className={`w-full rounded-md border px-2 py-1 text-sm ${isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                      />
                    </div>
                  </div>
                  <div className="flex items-start gap-3 py-2">
                    <Shield className={`w-4 h-4 mt-1.5 flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                    <div className="min-w-0 flex-1">
                      <label className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Tax Number</label>
                      <input
                        type="text"
                        value={overviewForm.tax_number ?? ''}
                        onChange={(e) => setOverviewForm((f) => ({ ...f, tax_number: e.target.value }))}
                        disabled={savingOverview}
                        className={`w-full rounded-md border px-2 py-1 text-sm ${isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                      />
                    </div>
                  </div>
                  <div className="flex items-start gap-3 py-2">
                    <Briefcase className={`w-4 h-4 mt-1.5 flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                    <div className="min-w-0 flex-1">
                      <label className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Industry</label>
                      <input
                        type="text"
                        value={overviewForm.industry ?? ''}
                        onChange={(e) => setOverviewForm((f) => ({ ...f, industry: e.target.value }))}
                        disabled={savingOverview}
                        className={`w-full rounded-md border px-2 py-1 text-sm ${isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                      />
                    </div>
                  </div>
                </>
              )}

              {entityType === 'person' && (
                <>
                  <InfoRow icon={User} label="Full Name" value={title} isDark={isDark} />
                  <InfoRow icon={Briefcase} label="Role" value={entity.job_title} isDark={isDark} />
                  <InfoRow icon={Users} label="Department" value={entity.department} isDark={isDark} />
                  <InfoRow icon={Mail} label="Email" value={entity.email} isDark={isDark} href={entity.email ? `mailto:${entity.email}` : undefined} />
                  <InfoRow icon={Phone} label="Phone" value={entity.phone || entity.mobile} isDark={isDark} href={entity.phone || entity.mobile ? `tel:${entity.phone || entity.mobile}` : undefined} />
                  <InfoRow icon={Building2} label="Stakeholder" value={parentContact?.company_name || parentContact?.contact_person || null} isDark={isDark} />
                </>
              )}

              {entityType === 'location' && (
                <>
                  <InfoRow icon={MapPin} label="Location" value={entity.location_name} isDark={isDark} />
                  <InfoRow icon={Briefcase} label="Type" value={entity.location_type} isDark={isDark} />
                  <InfoRow icon={Building2} label="Stakeholder" value={parentContact?.company_name || parentContact?.contact_person || null} isDark={isDark} />
                  <InfoRow icon={MapPin} label="Address" value={buildAddress(entity)} isDark={isDark} />
                  <InfoRow icon={Hash} label="Coordinates" value={formatCoordinates(entity.latitude, entity.longitude)} isDark={isDark} />
                </>
              )}

              {entityType === 'document' && (
                <>
                  <InfoRow icon={FileText} label="Document" value={entity.document_name} isDark={isDark} />
                  <InfoRow icon={Briefcase} label="Type" value={entity.document_type} isDark={isDark} />
                  <InfoRow icon={Building2} label="Stakeholder" value={parentContact?.company_name || parentContact?.contact_person || null} isDark={isDark} />
                  <InfoRow icon={MapPin} label="Linked Location" value={linkedLocation?.location_name || entity.location_name} isDark={isDark} />
                  <InfoRow icon={User} label="Linked Person" value={linkedPerson ? `${linkedPerson.first_name} ${linkedPerson.last_name}` : entity.person_name} isDark={isDark} />
                  <InfoRow icon={Calendar} label="Issue Date" value={formatDate(entity.issue_date)} isDark={isDark} />
                  <InfoRow icon={Calendar} label="Expiry Date" value={formatDate(entity.expiry_date)} isDark={isDark} />
                </>
              )}
            </div>
            {entityType === 'contact' && isEditingOverview && (
              <div className={`px-4 pb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <label className={`text-xs mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Notes</label>
                <textarea
                  value={overviewForm.notes ?? ''}
                  onChange={(e) => setOverviewForm((f) => ({ ...f, notes: e.target.value }))}
                  disabled={savingOverview}
                  rows={4}
                  placeholder="Add notes…"
                  className={`w-full rounded-lg border p-3 text-sm resize-none ${isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>
            )}
            {entityType === 'contact' && !isEditingOverview && (contactFieldOverride.notes ?? entity.notes) && (
              <div className={`px-4 pb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <div className={`rounded-lg p-3 text-sm whitespace-pre-wrap ${isDark ? 'bg-gray-900/60' : 'bg-gray-50'}`}>
                  {contactFieldOverride.notes ?? entity.notes}
                </div>
              </div>
            )}
            {entityType !== 'contact' && (entity.notes || entity.description) ? (
              <div className={`px-4 pb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <div className={`rounded-lg p-3 text-sm whitespace-pre-wrap ${isDark ? 'bg-gray-900/60' : 'bg-gray-50'}`}>
                  {entity.notes || entity.description}
                </div>
              </div>
            ) : null}
          </div>

          {mapLocations.length > 0 && (
            <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className={`px-4 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                <h3 className={`text-sm font-semibold flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                  <MapPin className="w-4 h-4 text-green-500" />
                  Map View
                </h3>
              </div>
              <div className="h-80">
                <GoogleMapsPicker
                  onLocationSelect={() => { }}
                  initialLat={String(mapInitialLocation?.latitude || '-25.8323022')}
                  initialLng={String(mapInitialLocation?.longitude || '28.2105906')}
                  existingGeofences={mapLocations.map((location) => ({
                    id: location.id,
                    name: location.location_name,
                    latitude: toFiniteNumber(location.latitude) ?? undefined,
                    longitude: toFiniteNumber(location.longitude) ?? undefined,
                    radius: toFiniteNumber(location.radius_meters) ?? 100,
                    location_type: location.location_type || 'stakeholder',
                  }))}
                  showSearch={false}
                />
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          {isSupplierLikeStakeholder && entityType === 'contact' && (
            <div className={`rounded-xl border ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className={`px-4 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>Supply Relationship</h3>
              </div>
              <div className="p-4 space-y-4">
                {loadingSupplierRelationships ? (
                  <div className="flex items-center justify-center py-10 text-sm text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading supplied items…
                  </div>
                ) : supplierRelationshipsError ? (
                  <div className={`rounded-lg p-3 text-sm ${isDark ? 'bg-red-900/20 text-red-200' : 'bg-red-50 text-red-700'}`}>
                    {supplierRelationshipsError}
                  </div>
                ) : supplierRelationshipStats ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <StatCard icon={Package} label="Mapped Items" value={supplierRelationshipStats.totalItems} isDark={isDark} tone="blue" />
                      <StatCard icon={CheckCircle2} label="Preferred On" value={supplierRelationshipStats.preferredCount} isDark={isDark} tone="green" />
                      <StatCard icon={Tag} label="Backups" value={supplierRelationshipStats.alternateCount} isDark={isDark} tone="purple" />
                      <StatCard icon={Calendar} label="Avg Lead" value={supplierRelationshipStats.avgLeadTime != null ? `${supplierRelationshipStats.avgLeadTime.toFixed(1)} d` : '—'} isDark={isDark} tone="amber" />
                    </div>

                    <div className={`rounded-lg border ${isDark ? 'border-gray-700 bg-gray-900/40' : 'border-gray-100 bg-gray-50'}`}>
                      <div className={`flex items-center justify-between px-3 py-2 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                        <p className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Recent / preferred supplied items</p>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setShowPerformanceDashboard(!showPerformanceDashboard)}
                            className="text-[11px] text-purple-600 hover:text-purple-700 font-medium"
                          >
                            {showPerformanceDashboard ? 'Hide Performance' : 'View Performance'}
                          </button>
                          <button
                            onClick={() => setProductAssignmentModalOpen(true)}
                            className="text-[11px] text-blue-600 hover:text-blue-700 font-medium"
                          >
                            + Add Products
                          </button>
                          <span className={`text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                            Last purchase {supplierRelationshipStats.lastPurchaseDate ? formatDate(supplierRelationshipStats.lastPurchaseDate) : '—'}
                          </span>
                        </div>
                      </div>
                      <div className="divide-y max-h-72 overflow-y-auto" style={{ borderColor: isDark ? '#374151' : '#e5e7eb' }}>
                        {sortedSupplierRelationships.slice(0, 6).map((relationship) => (
                          <div key={relationship.id} className="px-3 py-3">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className={`text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                                    {relationship.product_name || relationship.product_sku || relationship.work_item_id}
                                  </p>
                                  {relationship.is_preferred && (
                                    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${isDark ? 'bg-teal-900/30 text-teal-300' : 'bg-teal-100 text-teal-700'}`}>
                                      Preferred
                                    </span>
                                  )}
                                  {relationship.priority_rank != null && !relationship.is_preferred && (
                                    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${isDark ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                                      Rank #{relationship.priority_rank}
                                    </span>
                                  )}
                                </div>
                                <div className={`mt-1 flex flex-wrap items-center gap-3 text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                  <span>Product SKU: {relationship.product_sku || '—'}</span>
                                  <span>Supplier SKU: {relationship.supplier_sku || '—'}</span>
                                  <span>MOQ: {relationship.min_order_qty ?? '—'}</span>
                                  <span>Lead: {relationship.lead_time_days != null ? `${relationship.lead_time_days} d` : '—'}</span>
                                </div>
                                {(relationship.selection_reason || relationship.fallback_reason) && (
                                  <div className={`mt-2 space-y-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {relationship.selection_reason && (
                                      <p>
                                        <span className={`font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Preferred because:</span>{' '}
                                        {relationship.selection_reason}
                                      </p>
                                    )}
                                    {relationship.fallback_reason && (
                                      <p>
                                        <span className={`font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Use as backup when:</span>{' '}
                                        {relationship.fallback_reason}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className="text-right text-xs">
                                <p className={`font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                                  {relationship.cost_price != null ? formatMoney(relationship.cost_price, relationship.currency_code || 'ZAR') : '—'}
                                </p>
                                <p className={`${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                                  Last buy {relationship.last_purchase_date ? formatDate(relationship.last_purchase_date) : '—'}
                                </p>
                              </div>
                            </div>
                            {relationship.notes && (
                              <p className={`mt-2 text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                                {relationship.notes}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Performance Dashboard */}
                    {showPerformanceDashboard && contactIdForShortcuts !== null && (
                      <div className="mt-4">
                        <SupplierPerformanceDashboard
                          supplierContactId={contactIdForShortcuts}
                          supplierName={contactDisplayName}
                          isDark={isDark}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <div className={`rounded-lg border border-dashed px-4 py-8 text-center text-sm ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-300 text-gray-600'}`}>
                    No product sourcing relationships are linked to this {stakeholderContactType || 'stakeholder'} yet.
                  </div>
                )}
              </div>
            </div>
          )}

          {parentContact && entityType !== 'contact' && (
            <div className={`rounded-xl border ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className={`px-4 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>Owning Stakeholder</h3>
              </div>
              <div className="p-4">
                <div className={`rounded-lg p-3 ${isDark ? 'bg-gray-900/60' : 'bg-gray-50'}`}>
                  <p className={`font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                    {parentContact.company_name || parentContact.contact_person || 'Unnamed Stakeholder'}
                  </p>
                  <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {parentContact.contact_person || parentContact.industry || cf.stakeholder_type_label || 'Stakeholder'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {((entityType === 'contact' && relatedPeople.length > 0) || (entityType !== 'contact' && relatedPeople.length > 0)) && (
            <div className={`rounded-xl border ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className={`px-4 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>People</h3>
              </div>
              <div className="p-4 space-y-2 max-h-72 overflow-y-auto">
                {relatedPeople.slice(0, 8).map((person) => (
                  <div key={person.id} className={`rounded-lg border px-3 py-2 ${isDark ? 'border-gray-700 bg-gray-900/40' : 'border-gray-100 bg-gray-50'}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                          {person.first_name} {person.last_name}
                        </p>
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {person.job_title || person.department || 'Contact person'}
                        </p>
                        {(person.email || person.phone) && (
                          <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'} mt-0.5`}>
                            {person.email || person.phone}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {person.is_primary && (
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${isDark ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-100 text-amber-700'}`}>
                            Primary
                          </span>
                        )}
                        {onGrantPortalAccess && (person.email || person.phone) && (
                          <button
                            onClick={() => onGrantPortalAccess(person)}
                            className="p-1 text-gray-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/30 rounded transition-colors"
                            title="Grant Portal Access"
                          >
                            <Send className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {((entityType === 'contact' && relatedLocations.length > 0) || (entityType === 'person' && assignedLocations.length > 0) || (entityType === 'location' && relatedDocuments.length > 0)) && (
            <div className={`rounded-xl border ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className={`px-4 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                  {entityType === 'person' ? 'Assigned Locations' : 'Locations'}
                </h3>
              </div>
              <div className="p-4 space-y-2 max-h-72 overflow-y-auto">
                {(entityType === 'person' ? assignedLocations : relatedLocations).slice(0, 8).map((location) => (
                  <div key={location.id} className={`rounded-lg border px-3 py-2 ${isDark ? 'border-gray-700 bg-gray-900/40' : 'border-gray-100 bg-gray-50'}`}>
                    <p className={`text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{location.location_name}</p>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{buildAddress(location) || location.location_type || 'Location details unavailable'}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {relatedDocuments.length > 0 && entityType !== 'document' && (
            <div className={`rounded-xl border ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className={`px-4 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>Documents</h3>
              </div>
              <div className="p-4 space-y-2 max-h-72 overflow-y-auto">
                {relatedDocuments.slice(0, 8).map((document) => (
                  <div key={document.id} className={`rounded-lg border px-3 py-2 ${isDark ? 'border-gray-700 bg-gray-900/40' : 'border-gray-100 bg-gray-50'}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className={`text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{document.document_name}</p>
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{document.document_type || 'Document'}</p>
                      </div>
                      {document.is_verified ? <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" /> : null}
                    </div>
                    {document.expiry_date && (
                      <p className={`mt-1 text-xs ${document.is_expired ? 'text-red-500' : isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Expires {formatDate(document.expiry_date)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {entityType === 'document' && (entity.is_verified || entity.file_url) && (
            <div className={`rounded-xl border ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className={`px-4 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>Document Status</h3>
              </div>
              <div className="p-4 space-y-3">
                <div className={`rounded-lg p-3 ${entity.is_expired ? (isDark ? 'bg-red-900/20 text-red-300' : 'bg-red-50 text-red-700') : (isDark ? 'bg-green-900/20 text-green-300' : 'bg-green-50 text-green-700')}`}>
                  <div className="flex items-center gap-2 text-sm font-medium">
                    {entity.is_expired ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                    {entity.is_expired ? 'This document has expired' : 'This document is current'}
                  </div>
                </div>
                {entity.is_verified && (
                  <div className={`rounded-lg p-3 ${isDark ? 'bg-blue-900/20 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Shield className="w-4 h-4" />
                      Verified document
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Per-customer SLA Overrides — only for contact entities */}
      {entityType === 'contact' && contactIdForShortcuts !== null && (isCustomerStakeholder || isContractorStakeholder) && (
        <div className="mt-4">
          <ContactSlaOverridesPanel contactId={contactIdForShortcuts} isDark={isDark} />
        </div>
      )}

      {/* Customer Pricing modal */}
      {showPricingModal && contactIdForShortcuts !== null && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowPricingModal(false)} />
          <div
            className={`relative mx-4 flex max-h-[92vh] w-full max-w-7xl flex-col overflow-hidden rounded-xl shadow-2xl ${isDark ? 'bg-gray-900' : 'bg-white'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex items-center justify-between px-5 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center gap-2">
                <Tag className="w-4 h-4 text-blue-500" />
                <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                  Customer Pricing — {entity.company_name || entity.contact_person || 'Stakeholder'}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowPricingModal(false)}
                className={`rounded-md p-1.5 ${isDark ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-100'}`}
                aria-label="Close customer pricing"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <PriceListManagement
                showHeader={false}
                scopedCustomerId={contactIdForShortcuts}
                scopedCustomerLabel={contactDisplayName}
                hideCustomerPickerWhenScoped
                includeSharedWhenScoped={false}
                lockCustomerAssignmentWhenScoped
              />
            </div>
          </div>
        </div>
      )}

      {/* Stock & Items modal */}
      {showInventoryModal && contactIdForShortcuts !== null && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowInventoryModal(false)} />
          <div
            className={`relative mx-4 flex max-h-[92vh] w-full max-w-7xl flex-col overflow-hidden rounded-xl shadow-2xl ${isDark ? 'bg-gray-900' : 'bg-white'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex items-center justify-between px-5 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-blue-500" />
                <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                  Stock &amp; Items — {entity.company_name || entity.contact_person || 'Stakeholder'}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowInventoryModal(false)}
                className={`rounded-md p-1.5 ${isDark ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-100'}`}
                aria-label="Close stock & items"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <AdvancedInventoryView
                showHeader={false}
                moduleContext="operations"
                showWarehouses={false}
                scopedCustomerId={contactIdForShortcuts}
                scopedCustomerLabel={contactDisplayName}
                hideCustomerFilterWhenScoped
              />
            </div>
          </div>
        </div>
      )}

      {showSupplierItemsModal && contactIdForShortcuts !== null && isSupplierLikeStakeholder && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowSupplierItemsModal(false)} />
          <div
            className={`relative mx-4 flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl shadow-2xl ${isDark ? 'bg-gray-900' : 'bg-white'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex items-center justify-between px-5 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-teal-500" />
                <div>
                  <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                    Supplied Items — {contactDisplayName}
                  </h3>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Products currently linked to this supplier.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowSupplierItemsModal(false)}
                className={`rounded-md p-1.5 ${isDark ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-100'}`}
                aria-label="Close supplied items"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {loadingSupplierRelationships ? (
                <div className="flex items-center justify-center py-16 text-sm text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading supplied items…
                </div>
              ) : supplierRelationshipsError ? (
                <div className={`rounded-lg p-3 text-sm ${isDark ? 'bg-red-900/20 text-red-200' : 'bg-red-50 text-red-700'}`}>
                  {supplierRelationshipsError}
                </div>
              ) : sortedSupplierRelationships.length === 0 ? (
                <div className={`rounded-xl border border-dashed p-10 text-center ${isDark ? 'border-gray-700 bg-gray-800/40 text-gray-300' : 'border-gray-300 bg-gray-50 text-gray-600'}`}>
                  <Package className="mx-auto mb-3 h-10 w-10 opacity-60" />
                  <p className="text-sm font-medium">No supplied items linked yet.</p>
                  <p className="mt-1 text-xs opacity-80 mb-4">Link products to this stakeholder from the inventory item supplier tab to build a preferred/backup sourcing catalog.</p>
                  <button
                    onClick={() => setProductAssignmentModalOpen(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Products
                  </button>
                </div>
              ) : (
                <div className={`overflow-hidden rounded-xl border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
                    <thead className={`${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-50 text-gray-600'}`}>
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold">Item</th>
                        <th className="px-4 py-3 text-left font-semibold">Supplier SKU</th>
                        <th className="px-4 py-3 text-right font-semibold">Cost</th>
                        <th className="px-4 py-3 text-right font-semibold">Lead</th>
                        <th className="px-4 py-3 text-right font-semibold">MOQ</th>
                        <th className="px-4 py-3 text-right font-semibold">Last Buy</th>
                      </tr>
                    </thead>
                    <tbody className={`${isDark ? 'divide-y divide-gray-800 bg-gray-900/40' : 'divide-y divide-gray-100 bg-white'}`}>
                      {sortedSupplierRelationships.map((relationship) => (
                        <tr key={relationship.id}>
                          <td className="px-4 py-3 align-top">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className={`font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                                {relationship.product_name || relationship.product_sku || relationship.work_item_id}
                              </span>
                              {relationship.is_preferred && (
                                <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${isDark ? 'bg-teal-900/30 text-teal-300' : 'bg-teal-100 text-teal-700'}`}>
                                  Preferred
                                </span>
                              )}
                              {relationship.priority_rank != null && !relationship.is_preferred && (
                                <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${isDark ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                                  Rank #{relationship.priority_rank}
                                </span>
                              )}
                            </div>
                            <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                              Product SKU: {relationship.product_sku || '—'}
                            </p>
                            {(relationship.selection_reason || relationship.fallback_reason) && (
                              <div className={`mt-2 space-y-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                {relationship.selection_reason && (
                                  <p>
                                    <span className={`font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Preferred because:</span>{' '}
                                    {relationship.selection_reason}
                                  </p>
                                )}
                                {relationship.fallback_reason && (
                                  <p>
                                    <span className={`font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Use as backup when:</span>{' '}
                                    {relationship.fallback_reason}
                                  </p>
                                )}
                              </div>
                            )}
                          </td>
                          <td className={`px-4 py-3 align-top font-mono text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            {relationship.supplier_sku || '—'}
                          </td>
                          <td className={`px-4 py-3 text-right align-top ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                            {relationship.cost_price != null ? formatMoney(relationship.cost_price, relationship.currency_code || 'ZAR') : '—'}
                          </td>
                          <td className={`px-4 py-3 text-right align-top ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            {relationship.lead_time_days != null ? `${relationship.lead_time_days} d` : '—'}
                          </td>
                          <td className={`px-4 py-3 text-right align-top ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            {relationship.min_order_qty ?? '—'}
                          </td>
                          <td className={`px-4 py-3 text-right align-top ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            {relationship.last_purchase_date ? formatDate(relationship.last_purchase_date) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Product Assignment Modal */}
      {productAssignmentModalOpen && contactIdForShortcuts !== null && isSupplierLikeStakeholder && (
        <ProductAssignmentModal
          isOpen={productAssignmentModalOpen}
          onClose={() => {
            setProductAssignmentModalOpen(false);
            // Reload supplier relationships after closing
            if (contactIdForShortcuts) {
              const reloadRelationships = async () => {
                setLoadingSupplierRelationships(true);
                try {
                  const token = authService.getTokens()?.access_token;
                  const response = await fetch(`/api/v1/work-items/inventory/suppliers?supplier_contact_id=${contactIdForShortcuts}`, {
                    headers: {
                      ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                  });
                  const json = await response.json().catch(() => ({}));
                  if (response.ok && json?.success !== false) {
                    const rows = Array.isArray(json?.data) ? (json.data as Array<Record<string, unknown>>) : [];
                    setSupplierRelationships(rows.map((row) => ({
                      id: Number(row.id),
                      work_item_id: String(row.work_item_id || ''),
                      supplier_contact_id: Number(row.supplier_contact_id || contactIdForShortcuts),
                      supplier_name: typeof row.supplier_name === 'string' ? row.supplier_name : null,
                      product_name: typeof row.product_name === 'string' ? row.product_name : null,
                      product_sku: typeof row.product_sku === 'string' ? row.product_sku : null,
                      supplier_sku: typeof row.supplier_sku === 'string' ? row.supplier_sku : null,
                      cost_price: toFiniteNumber(row.cost_price),
                      currency_code: typeof row.currency_code === 'string' ? row.currency_code : null,
                      lead_time_days: toFiniteNumber(row.lead_time_days),
                      min_order_qty: toFiniteNumber(row.min_order_qty),
                      is_preferred: Boolean(row.is_preferred),
                      priority_rank: toFiniteNumber(row.priority_rank),
                      selection_reason: formatSupplierReason(row.selection_reason),
                      fallback_reason: formatSupplierReason(row.fallback_reason),
                      last_purchase_date: typeof row.last_purchase_date === 'string' ? row.last_purchase_date : null,
                      last_purchase_cost: toFiniteNumber(row.last_purchase_cost),
                      notes: typeof row.notes === 'string' ? row.notes : null,
                    })));
                  }
                } catch (error) {
                  console.error('[StakeholderOverview] failed to reload supplier relationships', error);
                } finally {
                  setLoadingSupplierRelationships(false);
                }
              };
              void reloadRelationships();
            }
          }}
          supplierContactId={contactIdForShortcuts}
          supplierName={contactDisplayName}
          onSuccess={() => {
            // Refresh work item to update stats
            if (onRefresh) {
              onRefresh();
            }
          }}
        />
      )}

      {/* Customer Templates modal */}
      {showTemplatesModal && contactIdForShortcuts !== null && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowTemplatesModal(false)} />
          <div
            className={`relative mx-4 flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl shadow-2xl ${isDark ? 'bg-gray-900' : 'bg-white'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex items-center justify-between px-5 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-purple-500" />
                <div>
                  <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                    Customer Templates — {contactDisplayName}
                  </h3>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Showing only templates assigned directly to this customer.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleCreateTemplate}
                  className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${isDark ? 'bg-purple-600 text-white hover:bg-purple-500' : 'bg-purple-500 text-white hover:bg-purple-600'
                    }`}
                >
                  <Plus className="w-4 h-4" />
                  New Template
                </button>
                <button
                  type="button"
                  onClick={() => setShowTemplatesModal(false)}
                  className={`rounded-md p-1.5 ${isDark ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-100'}`}
                  aria-label="Close customer templates"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div className={`rounded-xl border p-4 ${isDark ? 'border-purple-900/40 bg-purple-950/20' : 'border-purple-100 bg-purple-50'}`}>
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className={`text-sm font-medium ${isDark ? 'text-purple-200' : 'text-purple-900'}`}>
                      Templates are the reusable billing packs for this customer.
                    </p>
                    <p className={`text-xs ${isDark ? 'text-purple-300/80' : 'text-purple-700'}`}>
                      Only customer-assigned templates appear here. Use the finance workspace for shared or multi-customer template administration.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => void fetchCustomerTemplates()}
                    className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium ${isDark ? 'bg-gray-800 text-gray-100 hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                  >
                    {loadingTemplates ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Layers className="w-3.5 h-3.5" />}
                    Refresh List
                  </button>
                </div>
              </div>

              {loadingTemplates ? (
                <div className="flex items-center justify-center py-16 text-sm text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading customer templates…
                </div>
              ) : customerTemplates.length === 0 ? (
                <div className={`rounded-xl border border-dashed p-10 text-center ${isDark ? 'border-gray-700 bg-gray-800/40 text-gray-300' : 'border-gray-300 bg-gray-50 text-gray-600'}`}>
                  <Layers className="mx-auto mb-3 h-10 w-10 opacity-60" />
                  <p className="text-sm font-medium">No customer-assigned templates found for this customer yet.</p>
                  <p className="mt-1 text-xs opacity-80">Create a dedicated customer template here, or use the finance workspace if you need a shared template for multiple customers.</p>
                  <button
                    type="button"
                    onClick={handleCreateTemplate}
                    className={`mt-4 inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${isDark ? 'bg-purple-600 text-white hover:bg-purple-500' : 'bg-purple-500 text-white hover:bg-purple-600'}`}
                  >
                    <Plus className="w-4 h-4" />
                    Create First Template
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                  {customerTemplates.map((template) => {
                    const scopeMeta = getTemplateScopeMeta(template);
                    const itemCount = Array.isArray(template.items)
                      ? template.items.filter((item) => item.line_type === 'item').length
                      : 0;
                    const assignedContacts = Array.isArray(template.assigned_contacts) ? template.assigned_contacts : [];
                    const isBusy = templateActionId === template.id;

                    return (
                      <div
                        key={template.id}
                        className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-white'}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <h4 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                                {template.name}
                              </h4>
                              <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${getTemplateBadgeClass(scopeMeta.tone)}`}>
                                {scopeMeta.label}
                              </span>
                              {template.is_featured && (
                                <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${getTemplateBadgeClass('amber')}`}>
                                  Featured
                                </span>
                              )}
                              {template.is_system_template && (
                                <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${getTemplateBadgeClass('gray')}`}>
                                  System
                                </span>
                              )}
                            </div>
                            {template.description && (
                              <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                {template.description}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => void handleEditTemplate(template.id)}
                              disabled={isBusy}
                              className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium ${isDark ? 'bg-gray-700 text-gray-100 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} disabled:opacity-50`}
                            >
                              {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Edit3 className="w-3.5 h-3.5" />}
                              Edit
                            </button>
                            {!template.is_system_template && (
                              <button
                                type="button"
                                onClick={() => void handleDeleteTemplate(template)}
                                disabled={isBusy}
                                className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium ${isDark ? 'bg-red-900/30 text-red-200 hover:bg-red-900/50' : 'bg-red-50 text-red-700 hover:bg-red-100'} disabled:opacity-50`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                                Delete
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
                          <div>
                            <p className={`text-[11px] uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Items</p>
                            <p className={`text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{itemCount}</p>
                          </div>
                          <div>
                            <p className={`text-[11px] uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Used</p>
                            <p className={`text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{template.usage_count ?? 0} times</p>
                          </div>
                          <div>
                            <p className={`text-[11px] uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Payment Terms</p>
                            <p className={`text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{template.default_payment_terms || '—'}</p>
                          </div>
                          <div>
                            <p className={`text-[11px] uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Updated</p>
                            <p className={`text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{formatTemplateDate(template.updated_at || template.created_at)}</p>
                          </div>
                        </div>

                        {(template.category || (template.applicable_doc_types?.length ?? 0) > 0 || assignedContacts.length > 0) && (
                          <div className="mt-3 space-y-2">
                            {template.category && (
                              <div className="flex items-center gap-2">
                                <span className={`text-[11px] uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Category</span>
                                <span className={`text-xs ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{template.category}</span>
                              </div>
                            )}

                            {(template.applicable_doc_types?.length ?? 0) > 0 && (
                              <div className="flex flex-wrap gap-1.5">
                                {template.applicable_doc_types!.map((docType) => (
                                  <span
                                    key={`${template.id}-${docType}`}
                                    className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'}`}
                                  >
                                    {formatDocTypeLabel(docType)}
                                  </span>
                                ))}
                              </div>
                            )}

                            {assignedContacts.length > 0 && (
                              <p className={`text-[11px] ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                Assigned customers: {assignedContacts.slice(0, 3).map((contact) => contact.contact_name).join(', ')}
                                {assignedContacts.length > 3 ? ` +${assignedContacts.length - 3} more` : ''}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showBundleDesignerModal && contactIdForShortcuts !== null && (
        <BundleDesignerModal
          isOpen={showBundleDesignerModal}
          onClose={() => {
            setShowBundleDesignerModal(false);
            setEditingTemplate(null);
          }}
          bundle={editingTemplate}
          contactId={contactIdForShortcuts}
          lockedContactId={contactIdForShortcuts}
          lockedContactLabel={contactDisplayName}
          onSaved={() => {
            setShowBundleDesignerModal(false);
            setEditingTemplate(null);
            notify.success('Customer template saved');
            void fetchCustomerTemplates();
            setShowTemplatesModal(true);
          }}
        />
      )}
    </div>
  );
}
